/**
 * 짧은 알림.
 *
 * "복사됨" 처럼 **행동이 일어났다는 사실만** 알리는 용도다. 사용자가 읽고
 * 판단해야 하는 내용(오류 원인, 확인 요청)은 토스트로 보내지 않는다 — 몇 초 뒤
 * 사라져서 다시 볼 방법이 없다. 그런 것은 화면에 남는 자리에 쓴다.
 *
 * 모듈 수준 구독으로 만든 이유: 부르는 곳(컨텍스트 메뉴 안쪽)과 그리는 곳(App)이
 * 멀어서, Context 로 이으면 중간 컴포넌트들이 전부 통로 노릇을 하게 된다.
 */

export interface Toast {
  id: string;
  message: string;
}

type Listener = (toast: Toast) => void;

const listeners = new Set<Listener>();

/** 알림을 띄운다. 구독자가 없으면 조용히 사라진다(화면이 아직 없을 수 있다). */
export function showToast(message: string): void {
  const toast: Toast = { id: crypto.randomUUID(), message };
  for (const listener of listeners) listener(toast);
}

export function onToast(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 글자를 클립보드에 넣고 결과를 알린다.
 *
 * `navigator.clipboard` 는 보안 컨텍스트(https·localhost)에서만 있다. 없거나
 * 권한이 거절되면 **조용히 실패하지 않고** 그 사실을 알린다 — 복사된 줄 알고
 * 붙여넣으면 엉뚱한 것이 들어간다.
 */
export async function copyWithToast(
  text: string,
  label: string,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showToast(`${label} 복사됨`);
  } catch {
    showToast("복사하지 못했습니다. 직접 선택해 복사해 주세요.");
  }
}
